import {
  Button,
  Divider,
  FileInput,
  Group,
  MultiSelect,
  Paper,
  SimpleGrid,
  Stack,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconUpload } from "@tabler/icons-react";
import { useState } from "react";
import { Brand } from "../../../../gql/__generated__/graphql";

interface BrandEditFormProps {
  brand: Brand;
  onSave: (updatedBrand: Brand) => void;
  onCancel: () => void;
}

const BrandEditForm = ({
  brand,
  onSave,
  onCancel,
}: BrandEditFormProps) => {
  const [logoFile, setLogoFile] = useState<File | null>(null);

  const form = useForm({
    initialValues: {
      name: brand.name || "",
      logo: brand.logo || "",
      description: brand.description || "",
      categories: brand.categories || [],
    },
    validate: {
      name: (name: string) => (name ? null : "Brand name is required"),
      description: (description: string) =>
        description ? null : "Description is required",
      categories: (categories: string[]) =>
        !categories || categories.length === 0
          ? "Select at least one category"
          : categories.length > 3
          ? "Maximum 3 categories allowed"
          : null,
    },
  });

  const handleSubmit = (values: typeof form.values) => {
    // In a real app, you would handle file upload here
    // Merge the form values with the original brand to preserve non-editable fields
    const updatedBrand: Brand = {
      ...brand,
      ...values,
    };
    onSave(updatedBrand);
  };

  const categoryOptions = [
    { value: "fashion", label: "Fashion" },
    { value: "beauty", label: "Beauty" },
    { value: "fitness", label: "Fitness" },
    { value: "food", label: "Food & Beverage" },
    { value: "tech", label: "Technology" },
    { value: "lifestyle", label: "Lifestyle" },
    { value: "travel", label: "Travel" },
    { value: "gaming", label: "Gaming" },
    { value: "health", label: "Health & Wellness" },
    { value: "home", label: "Home & Decor" },
    { value: "sustainability", label: "Sustainability" },
  ];

  return (
    <Paper shadow="sm" p="lg" radius="md" withBorder>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="xl">
          <Title order={2}>Edit Brand Profile</Title>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <FileInput
              label="Brand Logo"
              placeholder="Upload new logo"
              accept="image/*"
              leftSection={<IconUpload size={16} />}
              value={logoFile}
              onChange={setLogoFile}
            />

            <TextInput
              label="Brand Name"
              placeholder="Your brand name"
              {...form.getInputProps("name")}
              required
            />
          </SimpleGrid>

          <MultiSelect
            label="Categories (up to 3)"
            placeholder="Select categories"
            data={categoryOptions}
            maxValues={3}
            {...form.getInputProps("categories")}
          />

          <Textarea
            label="Brand Description"
            placeholder="Tell us about your brand"
            minRows={4}
            {...form.getInputProps("description")}
            required
          />

          <Divider />

          <Group justify="flex-end" mt="xl">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" color="primary">
              Save Changes
            </Button>
          </Group>
        </Stack>
      </form>
    </Paper>
  );
};

export default BrandEditForm;
